/**
 * Slide-in panel listing every qualified or converted-lead job currently
 * in a failed state across all locations. Lets operators triage failures
 * globally without drilling into individual location dashboards.
 *
 * Failure definition (see `useGHLQualifierFailures` for SQL):
 *   - Qualified Lead: status='failed'
 *   - Converted Lead: status='failed' OR field_write_status starts with 'failed_'
 *
 * Each row links to the relevant detail page; the panel closes on navigation
 * so the operator lands directly on the contact's history.
 */

import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom'
import { AlertTriangle, ChevronRight, ExternalLink, RefreshCcw, Loader2 } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  useGHLQualifierFailures,
  useFailedGoogleAdsUploads,
  useRetryGoogleAdsConversion,
  useRetryQualifiedLead,
  useRetryConvertedLead,
  type FailedQualifiedJob,
  type FailedConvertedJob,
  type FailedGoogleAdsUpload,
} from '@/hooks/useGHLQualifierDashboard'
import { formatRelativeTime, truncateId } from '@/lib/ghl-qualifier'
import { useToast } from '@/hooks/use-toast'

interface Props {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}

/**
 * Row component shared between the Qualified + Converted tab. Renders the
 * contact identity, location, the failure-state summary (error message
 * + counts), and a chevron hint that the whole row is clickable.
 */
function FailureRow({
  contactName,
  contactEmail,
  contactId,
  locationName,
  locationId,
  primary,
  secondary,
  errorMessage,
  updatedAt,
  onClick,
  action,
}: {
  contactName: string | null
  contactEmail: string | null
  contactId: string
  locationName: string
  locationId: string
  primary: string
  secondary?: string
  errorMessage: string | null
  updatedAt: string
  onClick: () => void
  /** Optional action button rendered in the footer row (stops propagation itself). */
  action?: React.ReactNode
}) {
  const displayName = contactName?.trim() || truncateId(contactId)

  // A div-with-button-role rather than <button>: the optional action slot
  // renders a real <Button>, and nesting buttons is invalid HTML.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      className="w-full text-left rounded-md border border-border bg-card hover:bg-muted/40 transition-colors p-3 group cursor-pointer"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0 space-y-1">
          {/* Identity row */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm truncate">{displayName}</span>
            <Badge variant="outline" className="text-[10px] py-0 px-1.5 font-normal">
              {locationName}
            </Badge>
          </div>

          {/* Email / id subline */}
          {(contactEmail || contactId) && (
            <p className="text-xs text-muted-foreground truncate">
              {contactEmail ?? (
                <span className="font-mono">{truncateId(contactId, 14)}</span>
              )}
            </p>
          )}

          {/* Failure state summary — red badge with the primary state */}
          <div className="flex items-center gap-1.5 flex-wrap text-xs">
            <Badge className="bg-red-100 text-red-800 hover:bg-red-100 text-[10px] py-0 px-1.5">
              {primary}
            </Badge>
            {secondary && (
              <span className="text-muted-foreground">{secondary}</span>
            )}
            <span className="text-muted-foreground/60 ml-auto">
              {formatRelativeTime(updatedAt)}
            </span>
          </div>

          {/* Error message — [overflow-wrap:anywhere] (not break-words) so the
              unbroken JSON error blob doesn't inflate the row's min-content
              width and push the card past the panel edge */}
          {errorMessage && (
            <p className="text-xs text-red-700 whitespace-pre-wrap [overflow-wrap:anywhere] line-clamp-3 pt-0.5">
              {errorMessage}
            </p>
          )}

          {/* Location id reference for SQL/log-grep operators + row action */}
          <div className="flex items-center justify-between gap-2 pt-0.5">
            <p className="text-[10px] text-muted-foreground/50 font-mono truncate min-w-0">
              loc: {locationId}
            </p>
            {action}
          </div>
        </div>
        <ChevronRight className="h-4 w-4 mt-0.5 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors flex-shrink-0" />
      </div>
    </div>
  )
}

/** Footer action shared by all three tabs — stops the row's navigate click. */
function RetryNowButton({ retrying, onRetry }: { retrying: boolean; onRetry: () => void }) {
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={retrying}
      className="gap-1.5 h-7 shrink-0"
      onClick={(e) => {
        e.stopPropagation()
        onRetry()
      }}
    >
      {retrying ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <RefreshCcw className="h-3.5 w-3.5" />
      )}
      Retry now
    </Button>
  )
}

/**
 * Section wrapper — handles empty/loading state so the tab content stays
 * declarative.
 */
function FailureList<T>({
  items,
  isLoading,
  renderItem,
  emptyLabel,
}: {
  items: readonly T[]
  isLoading: boolean
  renderItem: (item: T) => React.ReactNode
  emptyLabel: string
}) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    )
  }
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <p className="text-sm">{emptyLabel}</p>
      </div>
    )
  }
  return <div className="space-y-2">{items.map(renderItem)}</div>
}

export function FailedJobsPanel({ open, onOpenChange }: Props) {
  const navigate = useNavigate()
  const routerLocation = useRouterLocation()
  // Only fetch while the sheet is open — avoids a background query for
  // operators who never open the panel.
  const { data, isLoading, refetch, isFetching } = useGHLQualifierFailures(open)
  const gads = useFailedGoogleAdsUploads(open)
  const retryUpload = useRetryGoogleAdsConversion()
  const retryQualified = useRetryQualifiedLead()
  const retryConverted = useRetryConvertedLead()
  const { toast } = useToast()

  /** Shared success/error toasts for the qualified/converted rerun buttons. */
  const retryCallbacks = {
    onSuccess: (result: { immediate: boolean }) =>
      toast({
        title: result.immediate ? 'Rerun complete' : 'Check re-queued',
        description: result.immediate
          ? 'The check was re-processed — refresh to see the new result.'
          : 'The qualifier service could not be reached; the cron will process it within ~2 minutes.',
      }),
    onError: (err: unknown) =>
      toast({
        title: 'Rerun failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive' as const,
      }),
  }

  const qualifiedCount = data?.qualified.length ?? 0
  const convertedCount = data?.converted.length ?? 0
  const googleAdsCount = gads.data?.length ?? 0
  const totalCount = qualifiedCount + convertedCount + googleAdsCount

  function navigateToQualified(id: string) {
    onOpenChange(false)
    navigate({
      pathname: `/admin/ghl-qualifier/qualified/${id}`,
      search: routerLocation.search,
    })
  }

  function navigateToConverted(id: string) {
    onOpenChange(false)
    navigate({
      pathname: `/admin/ghl-qualifier/converted/${id}`,
      search: routerLocation.search,
    })
  }

  function renderQualifiedRow(row: FailedQualifiedJob) {
    return (
      <FailureRow
        key={row.id}
        contactName={row.contact_name}
        contactEmail={row.contact_email}
        contactId={row.contact_id}
        locationName={row.location_name}
        locationId={row.location_id}
        primary={`Status: ${row.status}`}
        secondary={
          row.attempt_count
            ? `${row.attempt_count} attempt${row.attempt_count === 1 ? '' : 's'}`
            : undefined
        }
        errorMessage={row.error_message}
        updatedAt={row.updated_at}
        onClick={() => navigateToQualified(row.id)}
        action={
          row.status === 'failed' ? (
            <RetryNowButton
              retrying={retryQualified.isPending && retryQualified.variables?.id === row.id}
              onRetry={() => retryQualified.mutate({ id: row.id }, retryCallbacks)}
            />
          ) : undefined
        }
      />
    )
  }

  function renderConvertedRow(row: FailedConvertedJob) {
    // Failure surface: either the lifecycle status is failed (legacy stuck
    // row) OR the field-write attempt failed. Both are operationally
    // important and surface in the same panel so triage is one-stop.
    const isFieldWriteFailure =
      row.field_write_status?.startsWith('failed_') ?? false
    const primary = isFieldWriteFailure
      ? `Field-write: ${row.field_write_status}`
      : `Status: ${row.status}`
    const secondary = `Check ${row.check_count}/${row.max_checks}`
    const errorMessage = isFieldWriteFailure
      ? (row.field_write_error ?? row.error_message)
      : row.error_message

    return (
      <FailureRow
        key={row.id}
        contactName={row.contact_name}
        contactEmail={row.contact_email}
        contactId={row.contact_id}
        locationName={row.location_name}
        locationId={row.location_id}
        primary={primary}
        secondary={secondary}
        errorMessage={errorMessage}
        updatedAt={row.updated_at}
        onClick={() => navigateToConverted(row.id)}
        action={
          // Only true lifecycle failures are rerunnable — field-write
          // failures on a completed lifecycle need different remediation
          // and the retry RPC would no-op on them.
          row.status === 'failed' ? (
            <RetryNowButton
              retrying={retryConverted.isPending && retryConverted.variables?.id === row.id}
              onRetry={() => retryConverted.mutate({ id: row.id }, retryCallbacks)}
            />
          ) : undefined
        }
      />
    )
  }

  function renderGoogleAdsRow(row: FailedGoogleAdsUpload) {
    const displayName = row.contact_name?.trim() || truncateId(row.contact_id)
    const retrying = retryUpload.isPending && retryUpload.variables?.id === row.id
    return (
      <div key={row.id} className="rounded-md border border-border bg-card p-3 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm truncate">{displayName}</span>
          <Badge variant="outline" className="text-[10px] py-0 px-1.5 font-normal">
            {row.location_name}
          </Badge>
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100 text-[10px] py-0 px-1.5">
            {row.event_type}
          </Badge>
          <span className="text-muted-foreground/60 ml-auto text-xs">
            {formatRelativeTime(row.updated_at)}
          </span>
        </div>
        {row.error && (
          <p className="text-xs text-red-700 whitespace-pre-wrap [overflow-wrap:anywhere] line-clamp-3">
            {row.error}
          </p>
        )}
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] text-muted-foreground/50 font-mono">loc: {row.location_id}</p>
          <Button
            variant="outline"
            size="sm"
            disabled={retrying}
            className="gap-1.5 h-7"
            onClick={() =>
              retryUpload.mutate(
                { id: row.id },
                {
                  onSuccess: () =>
                    toast({
                      title: 'Upload re-queued',
                      description:
                        'The conversion will be retried on the next cron tick (~2 min).',
                    }),
                  onError: (err: unknown) =>
                    toast({
                      title: 'Retry failed',
                      description: err instanceof Error ? err.message : 'Unknown error',
                      variant: 'destructive',
                    }),
                }
              )
            }
          >
            {retrying ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCcw className="h-3.5 w-3.5" />
            )}
            Retry now
          </Button>
        </div>
      </div>
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-3 border-b">
          <div className="flex items-center justify-between gap-3">
            <div className="space-y-1">
              <SheetTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                Failed Jobs
              </SheetTitle>
              <SheetDescription>
                Every qualifier or converted-lead job currently in a failure state across
                all locations.
              </SheetDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              className="gap-1.5"
              aria-label="Refresh failed jobs list"
            >
              <RefreshCcw
                className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`}
              />
              Refresh
            </Button>
          </div>
        </SheetHeader>

        <Tabs defaultValue="qualified" className="flex-1 flex flex-col min-h-0">
          <div className="px-6 pt-3">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="qualified" className="gap-2">
                Qualified
                {qualifiedCount > 0 && (
                  <Badge
                    variant="secondary"
                    className="bg-red-100 text-red-800 hover:bg-red-100 h-5 min-w-5 px-1.5"
                  >
                    {qualifiedCount}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="converted" className="gap-2">
                Converted
                {convertedCount > 0 && (
                  <Badge
                    variant="secondary"
                    className="bg-red-100 text-red-800 hover:bg-red-100 h-5 min-w-5 px-1.5"
                  >
                    {convertedCount}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="google-ads" className="gap-2">
                Google Ads
                {googleAdsCount > 0 && (
                  <Badge
                    variant="secondary"
                    className="bg-red-100 text-red-800 hover:bg-red-100 h-5 min-w-5 px-1.5"
                  >
                    {googleAdsCount}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          <ScrollArea className="flex-1 px-6 py-4">
            <TabsContent value="qualified" className="mt-0 space-y-2">
              <FailureList
                items={data?.qualified ?? []}
                isLoading={isLoading}
                renderItem={renderQualifiedRow}
                emptyLabel="No qualified-lead jobs are currently failing. 🎉"
              />
            </TabsContent>
            <TabsContent value="converted" className="mt-0 space-y-2">
              <FailureList
                items={data?.converted ?? []}
                isLoading={isLoading}
                renderItem={renderConvertedRow}
                emptyLabel="No converted-lead jobs are currently failing. 🎉"
              />
            </TabsContent>
            <TabsContent value="google-ads" className="mt-0 space-y-2">
              <FailureList
                items={gads.data ?? []}
                isLoading={gads.isLoading}
                renderItem={renderGoogleAdsRow}
                emptyLabel="No Google Ads uploads are currently failing. 🎉"
              />
            </TabsContent>
          </ScrollArea>
        </Tabs>

        {/* Footer — quick summary of total failures across both queues */}
        <div className="border-t px-6 py-3 text-xs text-muted-foreground flex items-center justify-between">
          <span>
            {isLoading ? (
              <Skeleton className="h-3 w-32 inline-block" />
            ) : totalCount === 0 ? (
              'No failures across either queue'
            ) : (
              <>
                <span className="font-medium text-foreground">{totalCount}</span>{' '}
                {totalCount === 1 ? 'failure' : 'failures'} across both queues
              </>
            )}
          </span>
          {totalCount > 0 && (
            <span className="flex items-center gap-1 text-muted-foreground/70">
              Click a row to open detail
              <ExternalLink className="h-3 w-3" />
            </span>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

